import React, { FC, ReactNode, useEffect } from "react";
import { Box, Drawer, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CustomDrawerStyles from "./CustomDrawerStyles";
import { useAppSelector } from "../../store/hooks";

type OnClose = () => void;
type OnBack = () => void;

interface CustomDrawerProps {
  open: boolean;
  onClose: OnClose;
  title?: string;
  content?: ReactNode;
  drawerStyles?: object; // Additional styles for the drawer
  headerStyles?: object; // Additional styles for the header
  contentStyles?: object; // Additional styles for the content area
  onBack?: OnBack; // Optional back arrow functionality
}

const CustomDrawer: FC<CustomDrawerProps> = ({
  title,
  content,
  onClose,
  open,
  drawerStyles,
  headerStyles,
  contentStyles,
  onBack,
}) => {
  const { t } = useTranslation();
  const isChatOpen = useAppSelector((s) => s.chatUI.isOpen);

  // Notify chat panel about the active drawer title
  useEffect(() => {
    if (open) {
      const translated = title ? t(title) : "";
      window.dispatchEvent(
        new CustomEvent("chat:setContextTitle", {
          detail: { title: translated },
        })
      );
    } else {
      window.dispatchEvent(new CustomEvent("chat:clearDrawerTitle"));
    }
  }, [open, title, t]);

  return (
    <Drawer
      id="Drawer_drawer"
      data-test-id="test-Drawer_drawer"
      aria-label="Drawer"
      open={open}
      anchor={isChatOpen ? "left" : "right"}
      variant={isChatOpen ? "persistent" : "temporary"}
      sx={{
        ...CustomDrawerStyles.drawer,
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        zIndex: 1200,
        "& .MuiDrawer-paper": {
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "50%",
        },
        ...drawerStyles,
      }}
    >
      <Box
        id="Drawer_header"
        data-test-id="test-Drawer_header"
        aria-label="Drawer Header"
        sx={{
          ...CustomDrawerStyles.header,
          ...headerStyles,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
              }}
              aria-label="Go back"
            >
              <ArrowBack />
            </IconButton>
          )}
          <Typography
            id="Drawer_title"
            data-test-id="test-Drawer_title"
            aria-label="Drawer Title"
            variant="h6"
            sx={CustomDrawerStyles.title}
          >
            {title ? t(title) : ""}
          </Typography>
        </Box>
        <CloseIcon
          id="Drawer_close-button"
          data-test-id="test-Drawer_close-button"
          aria-label="Close Drawer"
          onClick={onClose}
          sx={{ cursor: "pointer" }}
        />
      </Box>

      <Box
        id="Drawer_content"
        data-test-id="test-Drawer_content"
        aria-label="Drawer Content"
        sx={{
          ...CustomDrawerStyles.content,
          ...contentStyles,
        }}
      >
        {content}
      </Box>
    </Drawer>
  );
};

export default CustomDrawer;
